import React, { useState, useEffect, useRef } from "react";
import { ListPageSkeleton } from "../../Components/Skeleton";
import SearchBar from "../../Components/SearchBar";
import Button from "../../Components/Button";
import { IoMdAdd } from "react-icons/io";
import { Modal } from "../../Components/Modal";
import { supabase } from "../../supabaseClient";
import { MdModeEditOutline, MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

function Vendor() {
  const [showModal, setShowModal] = useState(false);
  const [vendorName, setVendorName] = useState("");
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editVendor, setEditVendor] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);

  // Fetch all
  const fetchVendors = async () => {
    setIsLoading(true);
    const { data, error } = await supabase.from("Vendor").select("*");
    if (error) {
      setVendors([]);
    } else {
      setVendors(data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  // Add / Update
  const handleAddOrUpdateVendor = async () => {
    const name = vendorName.trim();
    if (!name) {
      Swal.fire({
        position: "top-end",
        icon: "warning",
        title: "Please enter a vendor name",
        showConfirmButton: false,
        timer: 3000,
        toast: true,
      });
      return;
    }

    setLoading(true);

    try {
      const normalized = name.toLowerCase();

      const { data: existing } = await supabase
        .from("Vendor")
        .select("id, name")
        .or(`lower(name).eq.${normalized}`);

      // Add mode duplicate
      if (!editVendor && existing && existing.length > 0) {
        setLoading(false);
        Swal.fire({
          position: "top-end",
          icon: "error",
          title: `"${name}" already exists`,
          showConfirmButton: false,
          timer: 2000,
          toast: true,
        });
        return;
      }

      // Edit mode duplicate
      if (editVendor && existing.length > 0) {
        const duplicate = existing.find((row) => row.id !== editVendor.id);
        if (duplicate) {
          setLoading(false);
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: `"${name}" already exists`,
            showConfirmButton: false,
            timer: 2000,
            toast: true,
          });
          return;
        }
      }

      // Update
      if (editVendor) {
        await supabase.from("Vendor").update({ name }).eq("id", editVendor.id);
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Vendor updated successfully",
          showConfirmButton: false,
          timer: 2000,
          toast: true,
        });
      }
      // Add
      else {
        await supabase.from("Vendor").insert([{ name }]);
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Vendor added successfully",
          showConfirmButton: false,
          timer: 2000,
          toast: true,
        });
      }

      setShowModal(false);
      setVendorName("");
      setEditVendor(null);
      fetchVendors();
    } catch (err) {
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "Something went wrong",
        showConfirmButton: false,
        timer: 2000,
        toast: true,
      });
    }
    setLoading(false);
  };

  // Delete
  const handleDeleteVendor = async (id) => {
    const { error } = await supabase.from("Vendor").delete().eq("id", id);
    if (error) {
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "Failed to delete vendor",
        timer: 2000,
        toast: true,
      });
    } else {
     
       Swal.fire({
                position: "top-end",
                icon: "success",
                title: "Vendor deleted successfully",
                showConfirmButton: false,
                timer: 3000,
                toast: true,
                background: "white",
                customClass: { popup: "shadow-lg rounded-md p-3" },
              });
      fetchVendors();
    }
  };

  useEffect(() => {
    if (showModal && inputRef.current) inputRef.current.focus();
  }, [showModal]);

  // Debounce hook
  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
  }

  // Search
  useEffect(() => {
    const fetchFilteredVendors = async () => {
      setIsLoading(true);
      let query = supabase.from("Vendor").select("*");

      if (debouncedSearch.trim() !== "") {
        query = query.ilike("name", `%${debouncedSearch}%`);
      }

      const { data } = await query;
      setVendors(data || []);
      setIsLoading(false);
    };

    fetchFilteredVendors();
  }, [debouncedSearch]);

  return (
    <div className="h-screen p-6 bg-white">
      {/* Controls */}
      <div className="flex flex-wrap justify-center items-center mb-4 gap-4">
        <div className="w-full sm:w-3/4 md:w-2/3 lg:w-1/3">
          <SearchBar
            placeholder="Search Vendor"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputClassName="py-3"
          />
        </div>

        <Button
          variant="primary"
       
        onClick={() => navigate("/vendor/create")}

          className="px-4 text-sm flex items-center gap-3"
        >
          <IoMdAdd /> Add Vendor
        </Button>
      </div>

      {/* List */}
      <div className="w-full mx-auto border p-4 h-[calc(100vh-7rem)] overflow-auto rounded-lg bg-white">
        <h1 className="text-3xl font-semibold uppercase font-serif border-b pb-3 mb-4">
          Vendors
        </h1>

        {isLoading ? (
          <ListPageSkeleton count={8} />
        ) : vendors.length === 0 ? (
          <p className="text-center font-semibold text-red-500">No data found</p>
        ) : (
          vendors.map((item) => (
            <div
              key={item.id}
              className="mb-3 bg-white px-4 py-2 rounded-lg border border-gray-400 hover:shadow-lg transition"
            >
              <div className="flex justify-between items-center">
                <h3 className="text-md font-semibold">{item.name}</h3>

                <div className="flex gap-3">
                  <button
                
                    onClick={() => navigate(`/vendor/${item.id}`)}

                    className="p-2 bg-yellow-100 text-yellow-500 rounded-full hover:bg-yellow-200"
                  >
                    <MdModeEditOutline />
                  </button>

                  <button
                    onClick={() => handleDeleteVendor(item.id)}
                    className="p-2 bg-red-100 text-red-500 rounded-full hover:bg-red-200"
                  >
                    <MdDelete />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      <Modal
        customClasses="w-full sm:max-w-lg md:max-w-2xl"
        showModal={showModal}
        setShowModal={setShowModal}
      >
        <div className="space-y-4 bg-gray-100 p-4 rounded-md border">
          <h2 className="text-xl font-semibold">
            {editVendor ? "Edit Vendor" : "Add Vendor"}
          </h2>

          <input
            type="text"
            ref={inputRef}
            placeholder="Enter vendor name"
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
          />

          <div className="flex justify-end gap-2">
            <Button variant="primary" disabled={loading} onClick={handleAddOrUpdateVendor}>
              {loading ? "Saving..." : editVendor ? "Update" : "Save"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Vendor;
